<template>
  <FlotoCrudContainer
    :fetch-fn="getMyChanges"
    as-table
    :columns="columns"
    allow-keyboard-navigation
    @select-item="handleViewDetail"
  >
    <template v-slot:add-controls="{ refreshList }">
      <FlotoPageHeader>
        <template v-slot:title>
          {{ $tc('my_changes') }}
        </template>
        <template v-slot:title-append>
          <FlotoScrollDropdown
            v-model="selectedSearch"
            avoid-keyboard-navigation
            class="header-selector search-selector"
            :options="changeSearchOptions"
            @change="refreshList"
          >
            <template v-slot:trigger="{ toggle }">
              <div
                class="inline-block ml-4 text-neutral cursor-pointer md-text-base font-normal"
                @click="toggle"
              >
                {{
                  __t(
                    (
                      changeSearchOptions.find(
                        (o) => o.key === selectedSearch
                      ) || {}
                    ).text
                  )
                }}
                <MIcon name="chevron-down" class="ml-1" />
              </div>
            </template>
            <template v-slot:menu-item="{ item, selectItem }">
              <a
                href="javascript:;"
                class="item-link flex-1"
                @click.stop="selectItem(item)"
              >
                {{ __t(item.text) }}
              </a>
            </template>
          </FlotoScrollDropdown>
        </template>
        <template v-slot:after-title>
          <MTooltip>
            <template v-slot:trigger>
              <MButton
                class="mx-1"
                variant="neutral-lighter"
                shape="circle"
                :shadow="false"
                @click="refreshList"
              >
                <MIcon name="sync" />
              </MButton>
            </template>
            {{ $t('refresh') }}
          </MTooltip>
        </template>
      </FlotoPageHeader>
    </template>
    <template v-slot:subject="{ item }">
      <td class="text-ellipsis">
        <ResourceListTitle
          :name="item.name"
          :subject="item.subject"
          external-link
          :link="
            $currentModule.getRoute('view-change', { params: { id: item.id } })
          "
        />
      </td>
    </template>
    <template v-slot:createdAt="{ item }">
      <td class="text-ellipsis">
        {{ !item.createdAt ? '---' : item.createdAt | datetime }}
      </td>
    </template>
    <template v-slot:assignee="{ item }">
      <td class="text-ellipsis">
        <FlotoTechnicianPicker :value="item.assignee" disabled />
      </td>
    </template>
    <template v-slot:statusId="{ item }">
      <td class="text-ellipsis">
        <FlotoStatusPicker
          :module-name="$constants.CHANGE"
          placeholder="---"
          :value="item.statusId"
          disabled
        />
      </td>
    </template>
    <template v-slot:priorityId="{ item }">
      <td class="text-ellipsis">
        <FlotoPriorityPicker
          :value="item.priorityId"
          placeholder="---"
          disabled
        />
      </td>
    </template>
    <template v-slot:changeTypeId="{ item }">
      <td class="text-ellipsis">
        <FlotoChangeTypePicker
          :value="item.changeTypeId"
          placeholder="---"
          disabled
        />
      </td>
    </template>
    <template v-slot:riskTypeId="{ item }">
      <td class="text-ellipsis">
        <FlotoRiskTypePicker
          :value="item.riskTypeId"
          placeholder="---"
          disabled
        />
      </td>
    </template>
    <template v-slot:approvalStatus="{ item }">
      <td class="text-ellipsis">
        <span v-if="item.approvalStatus">{{ $tc(item.approvalStatus) }}</span>
        <span v-else>---</span>
      </td>
    </template>
  </FlotoCrudContainer>
</template>

<script>
import ResourceListTitle from '@components/resource/resource-list-title'
import {
  buildRelationalQualificationStructure,
  buildFlatQualificationStructure,
} from '@data/qualification'
import { getMyChangesApi } from '../support-portal-api'
import { StatusComputed } from '../../../state/modules/status'
import {
  ChangeTypeComputed,
  ChangeTypeMethods,
} from '../../../state/modules/change-type'
import { SupportPortalConfigComputed } from '@state/modules/support-portal-config/helpers'

export default {
  name: 'MyChanges',
  components: { ResourceListTitle },
  data() {
    this.changeSearchOptions = [
      { text: 'all_open_changes', key: 'all_open_changes' },
      { text: 'all_emergency_changes', key: 'all_emergency_changes' },
      { text: 'all_changes', key: 'all_changes' },
    ]
    return {
      selectedSearch: 'all_changes',
    }
  },
  computed: {
    ...StatusComputed,
    ...ChangeTypeComputed,
    ...SupportPortalConfigComputed,
    closedStatusId() {
      return this.changeFindStatus('closed').id
    },
    columns() {
      return [
        { name: `${this.$tc('subject')}`, key: 'subject' },
        { name: `${this.$tc('created_date')}`, key: 'createdAt' },
        { name: `${this.$tc('assigned_to')}`, key: 'assignee' },
        { name: `${this.$tc('status')}`, key: 'statusId' },
        { name: `${this.$tc('priority')}`, key: 'priorityId' },
        { name: `${this.$tc('change_type')}`, key: 'changeTypeId' },
        { name: `${this.$tc('change_risk')}`, key: 'riskTypeId' },
        {
          name: `${this.$tc('approval')} ${this.$tc('status')}`,
          key: 'approvalStatus',
        },
      ]
    },
  },
  created() {
    this.fetchChangeTypes()
    if (!this.allowReqToAccessMyChanges) {
      this.$router.replace({ name: '404' })
    }
  },
  methods: {
    ...ChangeTypeMethods,
    handleViewDetail(item) {
      this.$router.push(
        this.$currentModule.getRoute('view-change', {
          params: { id: item.id },
        })
      )
    },
    buildSearchQualification() {
      const changeTypeId = (
        this.changeTypeOptions.find((t) => t.text === 'Emergency') || {}
      ).id
      switch (this.selectedSearch) {
        case 'all_changes':
          return []
        case 'all_open_changes':
          return [
            buildRelationalQualificationStructure(
              'statusId',
              'not_in',
              [this.closedStatusId],
              'long',
              'db'
            ),
          ]
        case 'all_emergency_changes':
          return [
            buildRelationalQualificationStructure(
              'changeTypeId',
              'in',
              [changeTypeId],
              'long',
              'db'
            ),
          ]
        default:
          return []
      }
    },
    getMyChanges(limit, offset) {
      let criterias
      criterias = buildFlatQualificationStructure(
        this.buildSearchQualification()
      )
      return getMyChangesApi(criterias, limit, offset)
    },
  },
}
</script>
